'use client'

import { createContext, useState, useEffect } from 'react'
import {
  safeGetItem,
  safeGetJSON,
  safeSetItem,
  safeRemoveItem,
} from '../helpers/safeStorage'
import { asLiveModel } from './CreateContext'

export const ChatCreateContext = createContext('')

export const ChatCreateProvider = ({ children }) => {
  const [model, setModel] = useState(() => {
    if (typeof window !== 'undefined') {
      return asLiveModel.decode(safeGetItem('model') || 'Pony Photo XL')
    }

    return 'Pony Photo XL'
  })

  const [chatId, setChatId] = useState('')
  // The source image's model, sent with create_character (see Create.jsx's chat hand-off).
  const [chatImageModel, setChatImageModel] = useState('')

  const [prompt, setPrompt] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('prompt') || ''
    }

    return ''
  })

  const [imageSrc, setImageSrc] = useState(() => {
    if (typeof window !== 'undefined') {
      // safeGetJSON: a bare JSON.parse of a corrupt stored value would throw during
      // render and crash-loop the provider until the user clears site data.
      return safeGetJSON('imageSrc', []) || []
    }

    return []
  })

  const [isLoading, setIsLoading] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('isLoading') === 'true'
    }

    return false
  })

  const [errorText, setErrorText] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('errorText') || ''
    }

    return ''
  })

  const [activeTab, setActiveTab] = useState(0)

  const [charImage, setCharImage] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('charImage') || null
    }

    return null
  })

  const [describeCharImage, setDescribeCharImage] = useState(() => {
    if (typeof window !== 'undefined') {
      // The `return` was missing here, so the stored value was read and discarded —
      // describeCharImage never survived a reload despite being persisted below.
      return safeGetItem('describeCharImage') || null
    }

    return null
  })

  const [bio, setBio] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('bio') || ''
    }

    return ''
  })

  const [personality, setPersonality] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('personality') || ''
    }

    return ''
  })

  const [scenario, setScenario] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('scenario') || ''
    }

    return ''
  })

  const [firstMessage, setFirstMessage] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('firstMessage') || ''
    }

    return ''
  })

  const [interests, setInterests] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('interests') || ''
    }
  })

  const [name, setName] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('name') || ''
    }

    return ''
  })

  const [occupation, setOccupation] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('occupation') || ''
    }

    return ''
  })

  const [sexuality, setSexuality] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('sexuality') || 'Straight'
    }

    return 'Straight'
  })

  const [customSexuality, setCustomSexuality] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('customSexuality') || ''
    }

    return ''
  })

  const [gender, setGender] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('gender') || 'Woman'
    }

    return 'Woman'
  })

  const [customGender, setCustomGender] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('customGender') || ''
    }

    return ''
  })

  const [ethnicity, setEthnicity] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('ethnicity') || 'Caucasian'
    }

    return 'Caucasian'
  })

  const [customEthnicity, setCustomEthnicity] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('customEthnicity') || ''
    }

    return ''
  })

  const [age, setAge] = useState(() => {
    if (typeof window !== 'undefined') {
      return parseInt(safeGetItem('age')) || 18
    }

    return 18
  })

  const [emotions, setEmotions] = useState(() => {
    if (typeof window !== 'undefined') {
      return parseInt(safeGetItem('emotions')) || 1
    }

    return 1
  })

  const [openness, setOpenness] = useState(() => {
    if (typeof window !== 'undefined') {
      return parseInt(safeGetItem('openness')) || 1
    }

    return 1
  })

  const [fashion, setFashion] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('fashion') || 'Casual'
    }

    return 'Casual'
  })

  const [customFashion, setCustomFashion] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('customFashion') || ''
    }

    return ''
  })

  const [bodyType, setBodyType] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('bodyType') || 'Medium'
    }

    return 'Medium'
  })

  const [customBodyType, setCustomBodyType] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('customBodyType') || ''
    }

    return ''
  })

  const [breastSize, setBreastSize] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('breastSize') || 'Medium'
    }

    return 'Medium'
  })

  const [locale, setLocale] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('locale') || ''
    }

    return ''
  })

  const [faceTab, setFaceTab] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('faceTab') || 'describe'
    }

    return 'describe'
  })

  const [continueOptions, setContinueOptions] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('continueOptions') === 'true'
    }

    return false
  })

  const [showAttributes, setShowAttributes] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('showAttributes')
      return storedValue !== null ? storedValue === 'true' : true
    }
    return true
  })

  const [weight, setWeight] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('weight')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [breasts, setBreasts] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('breasts')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [ass, setAss] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = safeGetItem('ass')
      return storedValue !== null ? parseInt(storedValue, 10) : 0
    }
    return 0
  })

  const [accent, setAccent] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('accent') || null
    }

    return null
  })

  const [voice, setVoice] = useState(() => {
    if (typeof window !== 'undefined') {
      return safeGetItem('voice') || null
    }

    return null
  })

  useEffect(() => {
    safeSetItem('model', model)
  }, [model])

  useEffect(() => {
    safeSetItem('imageSrc', JSON.stringify(imageSrc))
  }, [imageSrc])
  useEffect(() => {
    safeSetItem('isLoading', isLoading)
  }, [isLoading])
  useEffect(() => {
    safeSetItem('errorText', errorText)
  }, [errorText])
  useEffect(() => {
    safeSetItem('activeTab', activeTab)
  }, [activeTab])
  useEffect(() => {
    if (charImage === null) {
      safeRemoveItem('charImage')
    } else {
      safeSetItem('charImage', charImage)
    }
  }, [charImage])
  useEffect(() => {
    if (describeCharImage === null) {
      safeRemoveItem('describeCharImage')
    } else {
      safeSetItem('describeCharImage', describeCharImage)
    }
  }, [describeCharImage])
  useEffect(() => {
    safeSetItem('bio', bio)
  }, [bio])
  useEffect(() => {
    safeSetItem('personality', personality)
  }, [personality])
  useEffect(() => {
    safeSetItem('scenario', scenario)
  }, [scenario])
  useEffect(() => {
    safeSetItem('interests', interests)
  }, [interests])
  useEffect(() => {
    safeSetItem('name', name)
  }, [name])
  useEffect(() => {
    safeSetItem('occupation', occupation)
  }, [occupation])
  useEffect(() => {
    safeSetItem('sexuality', sexuality)
  }, [sexuality])
  useEffect(() => {
    safeSetItem('customSexuality', customSexuality)
  }, [customSexuality])
  useEffect(() => {
    safeSetItem('gender', gender)
  }, [gender])
  useEffect(() => {
    safeSetItem('customGender', customGender)
  }, [customGender])
  useEffect(() => {
    safeSetItem('ethnicity', ethnicity)
  }, [ethnicity])
  useEffect(() => {
    safeSetItem('customEthnicity', customEthnicity)
  }, [customEthnicity])
  useEffect(() => {
    safeSetItem('age', age.toString())
  }, [age])
  useEffect(() => {
    safeSetItem('emotions', emotions.toString())
  }, [emotions])
  useEffect(() => {
    safeSetItem('openness', openness.toString())
  }, [openness])
  useEffect(() => {
    safeSetItem('fashion', fashion)
  }, [fashion])
  useEffect(() => {
    safeSetItem('customFashion', customFashion)
  }, [customFashion])
  useEffect(() => {
    safeSetItem('bodyType', bodyType)
  }, [bodyType])
  useEffect(() => {
    safeSetItem('customBodyType', customBodyType)
  }, [customBodyType])
  useEffect(() => {
    safeSetItem('breastSize', breastSize)
  }, [breastSize])
  useEffect(() => {
    safeSetItem('locale', locale)
  }, [locale])
  useEffect(() => {
    safeSetItem('faceTab', faceTab)
  }, [faceTab])
  useEffect(() => {
    safeSetItem('continueOptions', continueOptions)
  }, [continueOptions])
  useEffect(() => {
    safeSetItem('weight', weight)
  }, [weight])

  useEffect(() => {
    safeSetItem('breasts', breasts)
  }, [breasts])

  useEffect(() => {
    safeSetItem('ass', ass)
  }, [ass])

  useEffect(() => {
    safeSetItem('showAttributes', showAttributes)
  }, [showAttributes])

  useEffect(() => {
    safeSetItem('voice', voice)
  }, [voice])

  useEffect(() => {
    safeSetItem('accent', accent)
  }, [accent])

  function resetStates() {
    setModel('Pony Photo XL')
    setPrompt('')
    setImageSrc([])
    setIsLoading(false)
    setErrorText('')
    setActiveTab(0)
    setCharImage(null)
    setDescribeCharImage(null)
    setBio('')
    setPersonality('')
    setInterests('')
    setName('')
    setOccupation('')
    setSexuality('Straight')
    setCustomSexuality('')
    setGender('Woman')
    setCustomGender('')
    setEthnicity('Caucasian')
    setCustomEthnicity('')
    setAge(21)
    setFashion('Casual')
    setCustomFashion('')
    setBodyType('Medium')
    setCustomBodyType('')
    setBreastSize('Medium')
    setLocale('')
    setFaceTab('describe')
    setContinueOptions(false)
    setScenario('')
    setOpenness(1)
    setEmotions(1)
    setShowAttributes(false)
    setWeight(0)
    setBreasts(0)
    setAss(0)
    setAccent(null)
    setVoice(null)
  }

  return (
    <ChatCreateContext.Provider
      value={{
        model,
        setModel,
        prompt,
        setPrompt,
        imageSrc,
        setImageSrc,
        isLoading,
        setIsLoading,
        errorText,
        setErrorText,
        activeTab,
        setActiveTab,
        charImage,
        setCharImage,
        describeCharImage,
        setDescribeCharImage,
        bio,
        setBio,
        personality,
        setPersonality,
        interests,
        setInterests,
        name,
        setName,
        occupation,
        setOccupation,
        sexuality,
        setSexuality,
        customSexuality,
        setCustomSexuality,
        gender,
        setGender,
        customGender,
        setCustomGender,
        ethnicity,
        setEthnicity,
        customEthnicity,
        setCustomEthnicity,
        age,
        setAge,
        emotions,
        setEmotions,
        fashion,
        setFashion,
        customFashion,
        setCustomFashion,
        bodyType,
        setBodyType,
        customBodyType,
        setCustomBodyType,
        breastSize,
        setBreastSize,
        locale,
        setLocale,
        faceTab,
        setFaceTab,
        continueOptions,
        setContinueOptions,
        resetStates,
        scenario,
        setScenario,
        openness,
        setOpenness,
        showAttributes,
        setShowAttributes,
        weight,
        setWeight,
        breasts,
        setBreasts,
        ass,
        setAss,
        accent,
        setAccent,
        voice,
        setVoice,
        chatId,
        setChatId,
        chatImageModel,
        setChatImageModel,
        firstMessage,
        setFirstMessage,
      }}
    >
      {children}
    </ChatCreateContext.Provider>
  )
}
